// Opt-in notifications: a Web Push subscription (VAPID) registered with the edge, which pushes
// invitations and answers to catalogue requests (never app updates, which are silent). Nothing
// about the person or their tastings is ever in a push. iOS delivers pushes only to an installed PWA, so there the
// state says "needs-install" rather than failing silently.
import { PushSubscribeRequest, PushUnsubscribeRequest, type PushLang } from "@proef/contract";
import { z } from "zod";
import { call } from "./edgeApi";
import { getDeviceId } from "./device";
import { isLocale } from "./i18n";
import { getAuthState } from "./social/auth";

export type PushState = "unsupported" | "unconfigured" | "needs-install" | "denied" | "off" | "on";
export type PushResult = "on" | "off" | "denied" | "failed";

/** The VAPID public key this build was configured with, if any. */
export function vapidPublicKey(): string {
  return import.meta.env.VITE_VAPID_PUBLIC_KEY?.trim() ?? "";
}

function runsStandalone(): boolean {
  const ios = (navigator as Navigator & { standalone?: boolean }).standalone === true;
  return ios || window.matchMedia("(display-mode: standalone)").matches;
}

function pushCapable(): boolean {
  return "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
}

/** base64url, as VAPID keys are written, to the bytes `subscribe` wants. */
export function keyToBytes(key: string): Uint8Array<ArrayBuffer> {
  const base64 = key.replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64.padEnd(Math.ceil(base64.length / 4) * 4, "="));
  return Uint8Array.from(raw, (char) => char.charCodeAt(0));
}

async function currentSubscription(): Promise<PushSubscription | null> {
  const registration = await navigator.serviceWorker.getRegistration();
  return (await registration?.pushManager.getSubscription()) ?? null;
}

/** What the About toggle should show right now. */
export async function pushState(): Promise<PushState> {
  if (!pushCapable()) {
    return /iPhone|iPad|iPod/.test(navigator.userAgent) && !runsStandalone()
      ? "needs-install"
      : "unsupported";
  }
  if (vapidPublicKey() === "") return "unconfigured";
  if (Notification.permission === "denied") return "denied";
  try {
    return (await currentSubscription()) === null ? "off" : "on";
  } catch {
    return "off";
  }
}

function pushLang(locale: string): PushLang {
  return isLocale(locale) ? locale : "nl";
}

/**
 * Registers a subscription with the edge. The call carries X-Device-Id, which is what links the
 * subscription to this device, so a "your winery was added" push reaches the phone that asked.
 */
async function tellEdge(subscription: PushSubscription, locale: string): Promise<void> {
  const json = subscription.toJSON();
  const body = PushSubscribeRequest.parse({
    endpoint: json.endpoint,
    keys: json.keys,
    lang: pushLang(locale),
  });
  await call(z.void(), "/v1/push/subscribe", { method: "POST", body: JSON.stringify(body) });
}

const LINKED_KEY = "proef.pushLinked";

/**
 * Re-registers a subscription granted before pushes were linked to a device (and keeps its language
 * current), once per device, subscription and language. It never asks for permission: with none
 * granted, or no subscription, it does nothing.
 */
export async function linkPushToDevice(locale: string): Promise<void> {
  try {
    if (!pushCapable() || vapidPublicKey() === "" || Notification.permission !== "granted") return;
    const subscription = await currentSubscription();
    if (subscription === null) return;
    const mark = `${getDeviceId()}|${subscription.endpoint}|${pushLang(locale)}`;
    if (window.localStorage.getItem(LINKED_KEY) === mark) return;
    await tellEdge(subscription, locale);
    window.localStorage.setItem(LINKED_KEY, mark);
  } catch {
    // Best effort: the next start tries again, and push is only ever an extra.
  }
}

/** Mirrors the just-registered anonymous subscription into the signed-in user's own
 * `user_push_subscriptions` row (account.ts), so broadcasts.feature's offline fan-out can reach
 * them -- no second VAPID dance, same browser subscription, best-effort: a failure here never
 * undoes the anonymous update-notification subscription that already succeeded. */
async function syncAccountPushOnEnable(
  subscription: PushSubscription,
  locale: string,
): Promise<void> {
  if (getAuthState().status !== "signedIn") return;
  const json = subscription.toJSON();
  const body = PushSubscribeRequest.parse({
    endpoint: json.endpoint,
    keys: json.keys,
    lang: pushLang(locale),
  });
  try {
    await call(z.void(), "/v1/account/push", { method: "POST", body: JSON.stringify(body) });
  } catch {
    // Best-effort: broadcasts still fall back to "undeliverable", never a broken toggle.
  }
}

/** The DELETE-side counterpart of syncAccountPushOnEnable, same best-effort contract. */
async function syncAccountPushOnDisable(endpoint: string): Promise<void> {
  if (getAuthState().status !== "signedIn") return;
  const body = PushUnsubscribeRequest.parse({ endpoint });
  try {
    await call(z.void(), "/v1/account/push", { method: "DELETE", body: JSON.stringify(body) });
  } catch {
    // Best-effort, same as above.
  }
}

/** Covers the order the PWA actually hits most often: a browser already has an anonymous update-
 * notification subscription from before sign-in, so signing in alone never re-runs `enablePush`'s
 * permission/subscribe dance. About's sign-in effect calls this once per session change; a no-op
 * when there is no existing subscription or the user is signed out. */
export async function syncAccountPushForSignedInUser(locale: string): Promise<void> {
  if (getAuthState().status !== "signedIn") return;
  const subscription = await currentSubscription();
  if (subscription === null) return;
  await syncAccountPushOnEnable(subscription, locale);
}

/** Asks for permission, subscribes and tells the edge; call it from a tap. */
export async function enablePush(locale: string): Promise<PushResult> {
  try {
    if ((await Notification.requestPermission()) !== "granted") return "denied";
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: keyToBytes(vapidPublicKey()),
    });
    try {
      await tellEdge(subscription, locale);
    } catch (error) {
      // The edge never heard of it, so a browser-side subscription would only notify nobody.
      await subscription.unsubscribe();
      throw error;
    }
    await syncAccountPushOnEnable(subscription, locale);
    return "on";
  } catch {
    return "failed";
  }
}

/** Drops the browser's subscription and the edge's copy of it. */
export async function disablePush(): Promise<PushResult> {
  try {
    const subscription = await currentSubscription();
    if (subscription === null) return "off";
    const body = PushUnsubscribeRequest.parse({ endpoint: subscription.endpoint });
    await subscription.unsubscribe();
    await call(z.void(), "/v1/push/subscribe", { method: "DELETE", body: JSON.stringify(body) });
    await syncAccountPushOnDisable(subscription.endpoint);
    return "off";
  } catch {
    return "failed";
  }
}
